import React from 'react';
import Banner from '../../common/Banner';
import {
  CAREGIVER_CODES,
  CAREGIVER_LABELS,
  FlowsheetGrid,
  computeFlowsheetTotals,
  formatHours,
  hoursMatch,
} from '../../../utils/flowsheetTotals';

export interface FlowSheetTotalsTarget {
  /** e.g. "PDN hours requested on the form" or "Approved per week" */
  label: string;
  /** Weekly PDN hours the grid should add up to; null/undefined skips the check. */
  hours: number | null | undefined;
}

interface FlowSheetTotalsBarProps {
  grid: FlowsheetGrid | null | undefined;
  /** Each target is compared with the weekly code-N (PDN) hours in the grid. */
  targets?: FlowSheetTotalsTarget[];
  className?: string;
}

/**
 * F00120 totals bar (Phase 8 §3.6c, EX-19): weekly hours per caregiver code (15-min cell = 0.25 h)
 * and an amber banner when the PDN (N) hours don't match the hours requested or approved.
 */
export const FlowSheetTotalsBar: React.FC<FlowSheetTotalsBarProps> = ({ grid, targets = [], className = '' }) => {
  const totals = computeFlowsheetTotals(grid);
  const pdnHours = totals.byCode.N;
  const mismatches = targets.filter(
    (t): t is { label: string; hours: number } =>
      typeof t.hours === 'number' && Number.isFinite(t.hours) && !hoursMatch(t.hours, pdnHours)
  );

  return (
    <div className={`space-y-2 ${className}`} data-testid="flowsheet-totals-bar">
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
        <span className="font-semibold text-slate-800">Hours per week:</span>
        {CAREGIVER_CODES.map(code => (
          <span
            key={code}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${
              totals.byCode[code] > 0 ? 'border-slate-300 bg-slate-50' : 'border-slate-200 text-slate-400'
            }`}
            data-testid={`flowsheet-total-${code}`}
          >
            <span className="font-semibold">{code}</span>
            <span>{CAREGIVER_LABELS[code]}</span>
            <span className="font-medium">{formatHours(totals.byCode[code])} h</span>
          </span>
        ))}
        <span className="ml-auto font-medium">Total {formatHours(totals.totalHours)} h</span>
      </div>
      {mismatches.length > 0 && (
        <Banner variant="warning" title="Flow sheet hours don't match" data-testid="flowsheet-totals-mismatch">
          <ul className="list-disc list-inside">
            {mismatches.map(t => (
              <li key={t.label}>
                PDN (N) cells add up to {formatHours(pdnHours)} h/week; {t.label} is {formatHours(t.hours)} h/week.
              </li>
            ))}
          </ul>
        </Banner>
      )}
    </div>
  );
};

export default FlowSheetTotalsBar;
